import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Schema } from 'effect';
import {
  BadRequestError,
  CaptchaError,
  NotFoundError,
  RateLimitedError,
  InternalServerError,
} from './errors';
import { DocumentTypeSchema } from './schemas/document-type';
import { TtsDetail } from './schemas/tts-dtos';
import { VoiceInput, VoiceModelId } from './schemas/voice';

export const PUBLIC_TOOL_TEXT_MAX_LENGTH = 10_000;
export const PUBLIC_TOOL_PDF_MAX_LENGTH = 25_000;
export const PUBLIC_TOOL_PDF_MAX_PAGES = 10;
export const PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
export const PUBLIC_TOOL_WEB_MAX_LENGTH = 25_000;

export const PublicToolUploadDocumentType = DocumentTypeSchema.pick([
  'pdf',
  'md',
]);
export const PUBLIC_TOOL_UPLOAD_DOCUMENT_TYPES =
  PublicToolUploadDocumentType.literals;
export const isPublicToolUploadDocumentType = Schema.is(
  PublicToolUploadDocumentType,
);

// Cloudflare Turnstile token issued by the client widget. Required on every
// submit endpoint; the API verifies it server-side before doing anything
// expensive. @see apps/api/src/services/turnstile.ts
const CfTurnstileToken = Schema.String;

const PublicToolTextToAudioPayload = Schema.Struct({
  text: Schema.String,
  voice: Schema.optional(VoiceInput),
  cf_turnstile_token: CfTurnstileToken,
});

const PublicToolPdfToAudioPayload = Schema.Struct({
  document_url: Schema.String,
  fileName: Schema.optional(Schema.String),
  voice: Schema.optional(VoiceInput),
  fileSizeBytes: Schema.optional(Schema.Int),
  cf_turnstile_token: CfTurnstileToken,
});

const PublicToolPdfUploadUrlPayload = Schema.Struct({
  filename: Schema.String,
  fileSizeBytes: Schema.optional(Schema.Int),
  cf_turnstile_token: CfTurnstileToken,
});

const PublicToolPdfUploadUrlResponse = Schema.Struct({
  status: Schema.Literal('success'),
  document_url: Schema.String,
  signed_upload_url: Schema.String,
  fields: Schema.StructWithRest(
    Schema.Struct({
      key: Schema.String,
      'Content-Type': Schema.String,
      'Content-Disposition': Schema.String,
    }),
    [Schema.Record(Schema.String, Schema.String)],
  ),
});

const PublicToolWebToAudioPayload = Schema.Struct({
  url: Schema.String,
  voice: Schema.optional(VoiceInput),
  cf_turnstile_token: CfTurnstileToken,
});

const ArticleMetadataResponse = Schema.Struct({
  url: Schema.String,
  title: Schema.NullOr(Schema.String),
  byline: Schema.NullOr(Schema.String),
  site_name: Schema.NullOr(Schema.String),
  char_count: Schema.Int,
});

const PublicToolReadAlongWord = Schema.Struct({
  text: Schema.String,
  start_ms: Schema.Int,
  end_ms: Schema.Int,
});

const PublicToolReadAlong = Schema.Struct({
  expires_at_ms: Schema.Int,
  title: Schema.String,
  source_label: Schema.optional(Schema.String),
  document_url: Schema.optional(Schema.String),
  source_url: Schema.optional(Schema.String),
  text: Schema.String,
  words: Schema.Array(PublicToolReadAlongWord),
});

const PublicToolTextToAudio200 = Schema.Struct({
  tool: Schema.Literal('text-to-audio'),
  audio_id: Schema.optional(Schema.String),
  tts: TtsDetail,
  read_along: Schema.optional(PublicToolReadAlong),
});

const PublicToolPdfToAudio200 = Schema.Struct({
  tool: Schema.Literal('pdf-to-audio'),
  audio_id: Schema.optional(Schema.String),
  source_document_url: Schema.optional(Schema.String),
  source_page_count: Schema.optional(Schema.Int),
  converted_page_count: Schema.optional(Schema.Int),
});

const PublicToolWebToAudio200 = Schema.Struct({
  tool: Schema.Literal('web-to-audio'),
  audio_id: Schema.optional(Schema.String),
  article: ArticleMetadataResponse,
  tts: TtsDetail,
  read_along: Schema.optional(PublicToolReadAlong),
});

export const PublicToolJobResult = Schema.Union([
  PublicToolTextToAudio200,
  PublicToolPdfToAudio200,
  PublicToolWebToAudio200,
]);

const PublicToolJobQueued = Schema.Struct({
  job_id: Schema.String,
  tool: Schema.Literals(['text-to-audio', 'pdf-to-audio', 'web-to-audio']),
  status: Schema.Literal('pending'),
});

/**
 * Early "read-along ready" marker, present while status='processing' once the
 * audio record is materialized. The reader synthesizes chunks on demand, so the
 * client can open the read-along page from this before the full result exists.
 */
const PublicToolJobPartial = Schema.Struct({
  tool: Schema.Literals(['text-to-audio', 'pdf-to-audio', 'web-to-audio']),
  audio_id: Schema.String,
  title: Schema.optional(Schema.String),
  expires_at_ms: Schema.optional(Schema.Int),
  source_page_count: Schema.optional(Schema.Int),
  /** Leading pages read aloud: the text layer stops at the last whole page that
   * fits `PUBLIC_TOOL_PDF_MAX_LENGTH`. Absent (OCR path) means every page up to
   * `PUBLIC_TOOL_PDF_MAX_PAGES`. */
  converted_page_count: Schema.optional(Schema.Int),
});

const PublicToolJobStatus = Schema.Struct({
  job_id: Schema.String,
  tool: Schema.Literals(['text-to-audio', 'pdf-to-audio', 'web-to-audio']),
  status: Schema.Literals(['pending', 'processing', 'completed', 'failed']),
  /** The voice the job synthesizes with, so a reader opened without `?voice`
   * plays the audio it generated. */
  voice: Schema.optional(VoiceModelId),
  result: Schema.NullOr(PublicToolJobResult),
  partial: Schema.optional(Schema.NullOr(PublicToolJobPartial)),
  error_message: Schema.NullOr(Schema.String),
});

const textToAudioEndpoint = HttpApiEndpoint.post(
  'TextToAudio',
  '/text-to-audio',
  {
    payload: PublicToolTextToAudioPayload,
    success: PublicToolJobQueued.pipe(HttpApiSchema.status(202)),
    error: [
      BadRequestError,
      CaptchaError,
      RateLimitedError,
      InternalServerError,
    ],
  },
)
  .annotate(OpenApi.Summary, 'Convert text to audio')
  .annotate(
    OpenApi.Description,
    `Unauthenticated text-to-audio endpoint for public SEO tools. Supports up to ${PUBLIC_TOOL_TEXT_MAX_LENGTH.toString()} characters.`,
  );

const pdfToAudioEndpoint = HttpApiEndpoint.post('PdfToAudio', '/pdf-to-audio', {
  payload: PublicToolPdfToAudioPayload,
  success: PublicToolJobQueued.pipe(HttpApiSchema.status(202)),
  error: [BadRequestError, CaptchaError, RateLimitedError, InternalServerError],
})
  .annotate(OpenApi.Summary, 'Convert a PDF or Markdown document to audio')
  .annotate(
    OpenApi.Description,
    `Unauthenticated document-to-audio endpoint for public SEO tools. Uploaded PDFs and Markdown files are processed through the same document conversion pipeline as the core product. Only the first ${PUBLIC_TOOL_PDF_MAX_PAGES.toString()} pages of a PDF are converted.`,
  );

const pdfUploadUrlEndpoint = HttpApiEndpoint.post(
  'PdfUploadUrl',
  '/pdf-upload-url',
  {
    payload: PublicToolPdfUploadUrlPayload,
    success: PublicToolPdfUploadUrlResponse.pipe(HttpApiSchema.status(200)),
    error: [
      BadRequestError,
      CaptchaError,
      RateLimitedError,
      InternalServerError,
    ],
  },
)
  .annotate(OpenApi.Summary, 'Get public document upload URL')
  .annotate(
    OpenApi.Description,
    `Get a presigned upload URL for the public PDF-to-audio tool. Supports PDF and Markdown files up to ${PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES.toString()} bytes.`,
  );

const webToAudioEndpoint = HttpApiEndpoint.post('WebToAudio', '/web-to-audio', {
  payload: PublicToolWebToAudioPayload,
  success: PublicToolJobQueued.pipe(HttpApiSchema.status(202)),
  error: [BadRequestError, CaptchaError, RateLimitedError, InternalServerError],
})
  .annotate(OpenApi.Summary, 'Convert a web article to audio')
  .annotate(
    OpenApi.Description,
    `Unauthenticated web-to-audio endpoint for public SEO tools. Supports up to ${PUBLIC_TOOL_WEB_MAX_LENGTH.toString()} extracted characters.`,
  );

const getJobEndpoint = HttpApiEndpoint.get('GetJob', '/jobs/:jobId', {
  params: Schema.Struct({ jobId: Schema.String }),
  success: PublicToolJobStatus.pipe(HttpApiSchema.status(200)),
  error: [BadRequestError, NotFoundError, InternalServerError],
}).annotate(OpenApi.Summary, 'Get public tool job status');

export const publicToolsGroup = HttpApiGroup.make('PublicTools')
  .add(textToAudioEndpoint)
  .add(pdfUploadUrlEndpoint)
  .add(pdfToAudioEndpoint)
  .add(webToAudioEndpoint)
  .add(getJobEndpoint)
  .prefix('/v2/public-tools');
